import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from './AnalyticsTracker';
import AuthModal from './auth/AuthModal';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSwitcher from './common/LanguageSwitcher';
import { OBusinessCard } from './OBusinessCard';
import { BusinessCard } from '../types/BusinessCard';

interface LandingPageProps {
  onGetStarted: () => void;
  /** Open the sign-in / sign-up dialog on arrival (used by /login and /register). */
  initialAuthMode?: 'login' | 'register';
}

/* ─── Demo card for the hero preview ─── */
import { demoCard } from '../data/demoCard';

/* ─── Features ─── */
const features = [
  {
    icon: (
      <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
      </svg>
    ),
    title: 'Easy Sharing',
    description: 'Share your card via QR code, NFC tap, or a simple link. No app needed to receive — works with any phone camera.',
  },
  {
    icon: (
      <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
    title: 'Customizable Design',
    description: 'Craft your unique O-shaped card with custom colors, fonts, and branding that reflects your professional identity.',
  },
  {
    icon: (
      <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-3.313a4.5 4.5 0 00-1.242-7.244l4.5-4.5a4.5 4.5 0 016.364 6.364l-1.757 1.757" />
      </svg>
    ),
    title: 'CRM & Integrations',
    description: 'Every card exchange becomes a trackable lead. Sync contacts with your CRM and never lose a connection.',
  },
];

/* ─── Pricing plans ─── */
const pricingPlans = [
  {
    name: 'Free',
    price: '$0',
    period: '/mo',
    features: ['1 digital business card', 'QR code sharing', 'Basic contact fields', 'Wallet pass'],
    cta: 'Start Free',
    popular: false,
  },
  {
    name: 'Pro',
    price: '$6.99',
    period: '/mo',
    features: ['Unlimited cards', 'Custom branding', 'Analytics dashboard', 'CRM integrations', 'Custom domain', 'NFC support'],
    cta: 'Start 14-Day Free Trial',
    popular: true,
  },
  {
    name: 'Teams',
    price: '$4.99',
    period: '/user/mo',
    features: ['Team management', 'Brand consistency', 'Admin dashboard', 'Bulk creation', 'SSO', 'Priority support'],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    features: ['White-label option', 'Full API access', 'Dedicated manager', 'SLA guarantee', 'Advanced analytics', 'Custom integrations'],
    cta: 'Contact Sales',
    popular: false,
  },
];

/* ═══════════════════════════════════════════════════════════════
   Landing Page Component
   ═══════════════════════════════════════════════════════════════ */
export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, initialAuthMode }) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(Boolean(initialAuthMode));
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>(initialAuthMode || 'register');
  const [email, setEmail] = useState('');
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    analytics.trackPageView('landing_page');
  }, []);

  const handleGetStarted = (plan: string = 'free') => {
    if (user) {
      onGetStarted();
    } else {
      setAuthModalMode('register');
      setIsAuthModalOpen(true);
    }
    analytics.track('landing_get_started_clicked', { plan });
  };

  const handleLogin = () => {
    setAuthModalMode('login');
    setIsAuthModalOpen(true);
    analytics.track('landing_login_clicked');
  };

  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault();
    setAuthModalMode('register');
    setIsAuthModalOpen(true);
    analytics.track('landing_signup_form_submitted', { email });
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white text-textPrimary font-body">
      {/* ─── Navbar ─── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-border" aria-label="Landing page navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold font-display tracking-[-0.02em] text-primary-dark">
              <span className="text-accent">O</span>BusinessCard
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo('features')} className="text-sm font-medium text-textSecondary hover:text-textPrimary transition-colors bg-transparent p-0">
              {t('landing.features')}
            </button>
            <button onClick={() => scrollTo('pricing')} className="text-sm font-medium text-textSecondary hover:text-textPrimary transition-colors bg-transparent p-0">
              {t('nav.pricing')}
            </button>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <button
                onClick={onGetStarted}
                className="px-5 py-2 bg-accent text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
              >
                {t('landing.go_to_app')}
              </button>
            ) : (
              <>
                <button onClick={handleLogin} className="hidden sm:inline-flex px-4 py-2 text-sm font-medium text-textSecondary hover:text-textPrimary transition-colors bg-transparent">
                  {t('landing.sign_in')}
                </button>
                <button
                  onClick={() => handleGetStarted('free')}
                  className="px-5 py-2 bg-accent text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                >
                  {t('cta.get_started')}
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ─── Hero Section ─── */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left – Copy */}
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-accent text-sm font-medium mb-6">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                {t('cta.no_credit_card')}
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-primary-dark leading-[1.1] mb-6">
                {t('landing.hero_title')}
              </h1>

              <p className="text-lg text-textSecondary leading-relaxed mb-8">
                A developer-first digital business card platform with a full API, deployment flexibility, and seamless CRM integrations that turns every contact exchange into a trackable lead.
              </p>

              {/* Signup form */}
              <form onSubmit={handleSignup} className="flex flex-col sm:flex-row gap-3 mb-6" aria-label="Sign up for a free card">
                <label htmlFor="hero-email" className="sr-only">Email address</label>
                <input
                  id="hero-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('landing.email_placeholder')}
                  className="flex-1 px-4 py-3 border border-border rounded-lg text-textPrimary placeholder:text-textMuted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent bg-white"
                  required
                  aria-required="true"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors shadow-sm whitespace-nowrap"
                  aria-label="Create your free digital business card"
                >
                  {t('cta.create_free_card')}
                </button>
              </form>

              <p className="text-sm text-textMuted">
                {t('cta.free_forever')}
              </p>

              {/* Trust bar: factual product promises only (no unverified usage or rating claims) */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 pt-8 border-t border-border/50 text-sm text-textMuted">
                <span>Free forever plan</span>
                <span>No app to install for the people you meet</span>
                <span>Works with any phone camera</span>
              </div>
            </div>

            {/* Right – Card Preview */}
            <div className="relative flex justify-center lg:justify-end">
              {/* Decorative blob */}
              <div className="absolute -top-12 -right-12 w-72 h-72 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-12 -left-12 w-56 h-56 bg-blue-100/40 rounded-full blur-3xl pointer-events-none" />

              <div className="relative z-10 w-full max-w-[380px]">
                <div className="relative bg-surfaceElevated rounded-3xl p-6 sm:p-8 shadow-xl border border-border/50">
                  <OBusinessCard card={demoCard} size="large" />
                  <div className="mt-4 text-center">
                    <p className="text-xs text-textMuted font-medium uppercase tracking-wider">
                      The O-Shaped Digital Card
                    </p>
                    <Link
                      to="/card/demo"
                      className="inline-block mt-2 text-sm font-semibold text-accent hover:underline"
                      onClick={() => analytics.track('landing_demo_card_clicked')}
                    >
                      Open the live demo card &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ─── Features Section ─── */}
      <section id="features" className="py-16 sm:py-20 lg:py-24 bg-white" aria-label="Features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.01em] text-primary-dark mb-4">
              {t('landing.features_title')}
            </h2>
            <p className="text-lg text-textSecondary">
              Stop losing connections. Start building relationships with tools designed for how professionals actually work.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, i) => (
              <div
                key={i}
                className="group p-6 lg:p-8 rounded-2xl border border-border/50 bg-white hover:bg-surfaceElevated hover:border-accent/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-5">
                  {feature.icon}
                </div>
                <h3 className="font-display text-xl font-semibold tracking-[-0.01em] text-primary-dark mb-3">
                  {feature.title}
                </h3>
                <p className="text-textSecondary leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ─── Pricing Overview ─── */}
      <section id="pricing" className="py-16 sm:py-20 lg:py-24 bg-white" aria-label="Pricing plans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.01em] text-primary-dark mb-4">
              {t('landing.pricing_title')}
            </h2>
            <p className="text-lg text-textSecondary">
              Start free, upgrade when you need more power.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                className={`relative rounded-2xl p-6 border transition-all duration-300 ${
                  plan.popular
                    ? 'border-accent bg-white shadow-lg ring-1 ring-accent/20 scale-[1.02]'
                    : 'border-border/50 bg-white hover:border-border hover:shadow-md'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-accent text-white text-xs font-semibold rounded-full">
                    Most Popular
                  </div>
                )}

                <h3 className="font-display text-lg font-semibold text-primary-dark mb-2">
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-textPrimary">{plan.price}</span>
                  <span className="text-textMuted text-sm">{plan.period}</span>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm text-textSecondary">
                      <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleGetStarted(plan.name.toLowerCase())}
                  className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    plan.popular
                      ? 'bg-accent text-white hover:bg-blue-600'
                      : 'border border-border text-textPrimary hover:bg-surfaceElevated'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/pricing"
              className="text-accent text-sm font-medium hover:underline"
            >
              View full pricing details &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* ─── CTA Banner ─── */}
      <section className="py-16 sm:py-20 bg-primary-dark" aria-label="Call to action">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-[-0.02em] text-white mb-4">
            {t('landing.final_cta_title')}
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Make your O-card in two minutes, share it from your phone, and see every new contact in one place.
          </p>

          <form onSubmit={handleSignup} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-6" aria-label="Get started with OBusinessCard">
            <label htmlFor="cta-email" className="sr-only">Email address</label>
            <input
              id="cta-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('landing.email_placeholder')}
              className="flex-1 px-4 py-3 rounded-lg text-textPrimary placeholder:text-textMuted focus:outline-none focus:ring-2 focus:ring-accent border-0 bg-white"
              required
              aria-required="true"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap"
              aria-label="Get started free"
            >
              {t('cta.get_started')}
            </button>
          </form>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <span>Free forever plan</span>
            <span>5-second setup</span>
            <span>No credit card required</span>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-primary-dark border-t border-gray-700 py-12" aria-label="Site footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <p className="font-display font-bold text-white text-lg mb-3">
                <span className="text-accent">O</span>BusinessCard
              </p>
              <p className="text-sm text-gray-400 leading-relaxed">
                {t('landing.tagline')}
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Product</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollTo('features')} className="text-sm text-gray-400 hover:text-white transition-colors bg-transparent p-0">Features</button></li>
                <li><button onClick={() => scrollTo('pricing')} className="text-sm text-gray-400 hover:text-white transition-colors bg-transparent p-0">Pricing</button></li>
                <li><Link to="/card/demo" className="text-sm text-gray-400 hover:text-white transition-colors">Demo card</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Company</h4>
              <ul className="space-y-2">
                <li><a href="mailto:obusinesscard@ozsystems.com.au" className="text-sm text-gray-400 hover:text-white transition-colors">Support</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="text-sm text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/cookie-policy" className="text-sm text-gray-400 hover:text-white transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-10 pt-6 text-center">
            <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} OBusinessCard. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ─── Auth Modal ─── */}
      {isAuthModalOpen && (
        <AuthModal
          mode={authModalMode}
          onClose={() => setIsAuthModalOpen(false)}
          onSuccess={() => {
            setIsAuthModalOpen(false);
            onGetStarted();
          }}
          onSwitchMode={() => setAuthModalMode(authModalMode === 'login' ? 'register' : 'login')}
        />
      )}
    </div>
  );
};

export default LandingPage;
